import type {
  WebMapCategoricalStyle,
  WebMapCategoryStyle,
  WebMapLayerConfig,
} from '../types/webMap';

export const MAX_WEB_MAP_CATEGORIES = 20;

export const CATEGORY_COLOR_PALETTE = [
  '#1976d2',
  '#9c27b0',
  '#00acc1',
  '#ef6c00',
  '#2e7d32',
  '#c62828',
  '#5d4037',
  '#3949ab',
  '#ad1457',
  '#00897b',
  '#6d4c41',
  '#7b1fa2',
  '#0277bd',
  '#558b2f',
  '#f9a825',
  '#d84315',
  '#4527a0',
  '#00695c',
  '#9e9d24',
  '#546e7a',
];

/** `mapbox://styles/mapbox/streets-v12` → `mapbox/streets-v12` for REST API paths. */
export function mapboxStylePathFromUrl(mapboxStyleUrl: string): string {
  const prefix = 'mapbox://styles/';
  if (mapboxStyleUrl.startsWith(prefix)) {
    return mapboxStyleUrl.slice(prefix.length);
  }
  return 'mapbox/streets-v12';
}

export function mapboxSpriteJsonUrl(stylePath: string, accessToken: string): string {
  return `https://api.mapbox.com/styles/v1/${stylePath}/sprite.json?access_token=${encodeURIComponent(accessToken)}`;
}

export function mapboxSpritePngUrl(stylePath: string, accessToken: string): string {
  return `https://api.mapbox.com/styles/v1/${stylePath}/sprite.png?access_token=${encodeURIComponent(accessToken)}`;
}

/** Small static-map preview for basemap thumbnails (Mapbox Static Images API). */
export function mapboxBasemapPreviewUrl(
  stylePath: string,
  accessToken: string,
  width = 140,
  height = 88,
): string {
  const lon = -98.5;
  const lat = 39.8;
  const zoom = 2;
  return `https://api.mapbox.com/styles/v1/${stylePath}/static/${lon},${lat},${zoom}/${width}x${height}@2x?access_token=${encodeURIComponent(accessToken)}`;
}

/**
 * react-map-gl updates GeoJSON sources via setData() only. Options like `cluster` and
 * `clusterRadius` are applied on addSource — toggling cluster mode or changing those
 * props requires a remounted <Source key=…>.
 */
export function webMapGeoJsonSourceKey(layerSourceId: string, lyr: WebMapLayerConfig): string {
  const mode = lyr.displayMode ?? 'point';
  if (mode === 'cluster') {
    return `${layerSourceId}__clu_${lyr.clusterRadius ?? 50}_${lyr.clusterMaxZoom ?? 14}`;
  }
  return `${layerSourceId}__plain`;
}

/** Icon ids present on mapbox://styles/mapbox/streets-v12 (core Mapbox basemaps). */
export const MAPBOX_STYLE_SPRITE_ICONS: string[] = [
  'dot-11',
  'marker',
  'airport',
  'bank',
  'bar',
  'bus',
  'cafe',
  'campsite',
  'car',
  'college',
  'communications-tower',
  'ferry',
  'fire-station',
  'fuel',
  'harbor',
  'heliport',
  'hospital',
  'library',
  'lighthouse',
  'lodging',
  'mountain',
  'park',
  'parking',
  'pharmacy',
  'police',
  'rail',
  'restaurant',
  'rocket',
  'school',
  'shop',
  'town-hall',
];

/** Map old / invalid picker ids to a sprite that exists on core Mapbox styles. */
export function resolveSavedSpriteIcon(id: string | undefined): string | undefined {
  if (!id) return undefined;
  if (MAPBOX_STYLE_SPRITE_ICONS.includes(id)) return id;
  const legacy: Record<string, string> = {
    circle: 'dot-11',
    star: 'marker',
    triangle: 'marker',
    square: 'marker',
    diamond: 'marker',
    heart: 'marker',
    water: 'harbor',
    dam: 'marker',
  };
  return legacy[id] ?? 'marker';
}

function categoryPropertyExpression(field: string): unknown[] {
  return ['to-string', ['coalesce', ['get', field], '']];
}

export function deriveCategoryStyles(
  features: GeoJSON.Feature[],
  field: string,
  existing?: WebMapCategoricalStyle,
): WebMapCategoryStyle[] {
  const existingByValue = new Map(existing?.categories.map((item) => [item.value, item]) ?? []);
  const values = new Set<string>();

  for (const feature of features) {
    const raw = feature.properties?.[field];
    if (raw == null || typeof raw === 'object') continue;
    values.add(String(raw));
  }

  return Array.from(values)
    .sort((a, b) => a.localeCompare(b, undefined, { numeric: true, sensitivity: 'base' }))
    .slice(0, MAX_WEB_MAP_CATEGORIES)
    .map((value, index) => {
      const saved = existingByValue.get(value);
      return saved ?? {
        value,
        label: value,
        color: CATEGORY_COLOR_PALETTE[index % CATEGORY_COLOR_PALETTE.length],
      };
    });
}

export function categoryColorExpression(
  layer: WebMapLayerConfig,
  fallbackColor: string,
): string | unknown[] {
  const style = layer.categoricalStyle;
  if (!style?.field || style.categories.length === 0) return fallbackColor;
  return [
    'match',
    categoryPropertyExpression(style.field),
    ...style.categories.flatMap((item) => [item.value, item.color]),
    style.fallbackColor || fallbackColor,
  ];
}

export function categoryIconExpression(layer: WebMapLayerConfig): string | unknown[] | undefined {
  const style = layer.categoricalStyle;
  const hasCategoryIcon = style?.categories.some((item) => Boolean(item.icon));
  if (!style?.field || !hasCategoryIcon) return resolveSavedSpriteIcon(layer.mapboxIcon);

  const fallback = resolveSavedSpriteIcon(style.fallbackIcon)
    ?? resolveSavedSpriteIcon(layer.mapboxIcon)
    ?? 'dot-11';
  return [
    'match',
    categoryPropertyExpression(style.field),
    ...style.categories.flatMap((item) => [
      item.value,
      resolveSavedSpriteIcon(item.icon) ?? fallback,
    ]),
    fallback,
  ];
}

export function heatmapWeightPaint(weightProp: string | undefined): Record<string, unknown> {
  if (!weightProp) return {};
  const w: unknown[] = [
    'interpolate',
    ['linear'],
    ['coalesce', ['to-number', ['get', weightProp]], 0],
    0,
    0,
    1,
    0.05,
    10,
    0.25,
    100,
    0.6,
    1000,
    1,
  ];
  return { 'heatmap-weight': w };
}
